import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Media, PageHero, Reveal, SectionHeading } from "@/components/site/Primitives";
import { CtaBanner, FAQ, Icon, TrustStrip } from "@/components/site/Sections";
import { PriceTable } from "@/components/site/PriceTable";
import { IMAGES, waLink } from "@/lib/site";
import { EXTRAS, FLEET_FEATURES, VEHICLES } from "@/lib/content";
import { WhatsAppIcon } from "@/components/site/BrandIcons";

const Anywhere = () => (
  <section id="anywhere" className="container-x section-y scroll-mt-24" data-testid="transfers-anywhere-section">
    <div className="grid items-center gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <Media img={IMAGES.anywhere} className="aspect-[4/3]" />
      </Reveal>
      <div className="lg:col-span-5 lg:col-start-8">
        <SectionHeading eyebrow="Beyond the airport" title="Private transfers, anywhere on the island" text="Dinner in the Old Town. A day at a hidden beach. A ride to a mountain village. Tell us where, we take care of the rest." />
        <Reveal delay={0.1} className="mt-8 flex flex-wrap gap-3">
          <Link to="/booking?pickup=__other&dropoff=__other" className="btn btn-dark" data-testid="request-quote-button">Request a quote</Link>
          <a href={waLink("Hello Alpha Transfer, I would like a quote for a private transfer.")} target="_blank" rel="noreferrer" className="btn btn-outline" data-testid="quote-whatsapp-button">
            <WhatsAppIcon className="h-4 w-4" /> Ask on WhatsApp
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

const VehicleCard = ({ v, i }) => (
  <Reveal delay={i * 0.1}>
    <article className="group" data-testid={`vehicle-option-${v.id}`}>
      <Media img={v.img} className="aspect-[4/3]" imgClassName="transition-transform duration-1000 group-hover:scale-105" />
      <div className="mt-6 flex items-baseline justify-between gap-4">
        <h3 className="font-serif text-3xl text-ink">{v.title}</h3>
        <span className="flex items-center gap-2 text-sm text-slate-600" data-testid={`vehicle-seats-${v.id}`}>
          <Icon name="Users" className="h-4 w-4 text-gold" /> Up to {v.seats} passengers
        </span>
      </div>
      <p className="mt-2 text-slate-600">{v.text}</p>
    </article>
  </Reveal>
);

const Vehicle = () => (
  <section className="bg-silver/60 py-20 md:py-28" data-testid="transfers-vehicle-section">
    <div className="container-x">
      <SectionHeading eyebrow="Your vehicle" title="The right vehicle for every group" text="Travelling as a couple, a family or a group of friends? Choose the size that fits. Every vehicle is private, air conditioned and spotless." />
      <div className="mt-12 grid gap-x-5 gap-y-12 md:grid-cols-3">
        {VEHICLES.map((v, i) => <VehicleCard key={v.id} v={v} i={i} />)}
      </div>
      <div className="mt-20 grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-testid="vehicle-features">
        {FLEET_FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={(i % 3) * 0.05}>
            <Icon name={f.icon} className="h-7 w-7 text-gold" />
            <h3 className="mt-4 font-serif text-2xl text-ink">{f.title}</h3>
            <p className="mt-2 text-slate-600">{f.text}</p>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-14 flex flex-wrap gap-3 border-t border-slate-200 pt-10">
        {EXTRAS.map((e) => (
          <span key={e.label} className="flex items-center gap-3 rounded-full border border-gold-border bg-white px-4 py-2 text-sm text-ink">
            <Icon name={e.icon} className="h-4 w-4 text-gold" /> {e.label}
          </span>
        ))}
      </Reveal>
      <Link to="/booking" className="btn btn-dark mt-10" data-testid="vehicle-book-button">
        Book your transfer <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </section>
);

export default function Transfers() {
  return (
    <>
      <Seo
        title="Rhodes Airport Transfer & Private Transfers | Fixed Prices"
        description="Private transfer Rhodes airport (RHO) and port, 24/7. Fixed prices per vehicle: Rhodes Town €36, Ialysos €40, Faliraki €70. Flight tracking and meet & greet."
        path="/transfers"
      />
      <PageHero
        eyebrow="Transfers"
        title="Private transfers, 24/7"
        text="From and to Rhodes Airport (RHO) and the Port, plus every ride you need on holiday: hotels, restaurants, beaches, villages and beyond. Always private. Always on time."
        img={IMAGES.airport}
      >
        <Link to="/booking" className="btn btn-gold" data-testid="transfers-book-button">Book a transfer</Link>
        <a href="#prices" className="btn btn-outline" data-testid="transfers-prices-link">See prices</a>
      </PageHero>
      <div className="container-x mt-10"><TrustStrip /></div>

      <section id="prices" className="container-x section-y scroll-mt-24" data-testid="transfers-prices-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="Fixed prices" title="From Rhodes Airport" text="One price for the whole vehicle. No meters, no surprises." />
            <Reveal delay={0.1} className="card-soft mt-8 border border-gold-border bg-gold-light p-6" data-testid="price-note">
              <p className="text-sm leading-relaxed text-ink">
                Prices are per vehicle, not per person. For other destinations, request a quote.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                {["Same price day and night", "Child seats free", "Waiting time for delays included"].map((x) => (
                  <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> {x}</li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-8"><PriceTable /></Reveal>
        </div>
      </section>
      <Anywhere />
      <Vehicle />
      <FAQ />
      <div className="pt-20 md:pt-28" />
      <CtaBanner />
    </>
  );
}
